import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Container from "@mui/material/Container";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import { useQueryClient } from "@tanstack/react-query";
import {
  useCreateOrganization,
  getListOrganizationsQueryKey,
} from "../../api/generated/generated";
import { useAuth } from "../../contexts/AuthContext";
import { getErrorMessage } from "../../api/client";

export function OnboardingPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, refreshToken } = useAuth();
  const createOrg = useCreateOrganization({
    mutation: {
      onSuccess: async () => {
        await refreshToken();
        queryClient.invalidateQueries({
          queryKey: getListOrganizationsQueryKey(),
        });
      },
    },
  });
  const [name, setName] = useState("");
  const [error, setError] = useState("");

  // Org creation is bootstrap-only: a user who already has an org should never land on the
  // create form (spec 035). Additional orgs are provisioned by super-admins from the admin area.
  if (user?.orgId) {
    return <Navigate to="/" replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (name.trim().length < 3) {
      setError("Organization name must be at least 3 characters");
      return;
    }

    try {
      await createOrg.mutateAsync({ data: { name: name.trim() } });
      navigate("/");
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  // Non-admins cannot create orgs — they need to be assigned one on approval
  if (!user?.superAdmin) {
    return (
      <Container maxWidth="sm">
        <Box
          sx={{
            mt: 8,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Typography variant="h4" gutterBottom>
            Account Pending Review
          </Typography>
          <Typography
            sx={{
              color: "text.secondary",
              mb: 2
            }}>
            Your account is pending administrator review. You will be granted
            access once an administrator has approved your account and assigned
            you to an organization.
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          mt: 8,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Typography variant="h4" gutterBottom>
          Welcome to Hanover Sales Coach
        </Typography>
        <Typography
          sx={{
            color: "text.secondary",
            mb: 4
          }}>
          Create an organization to get started.
        </Typography>

        <Card sx={{ width: "100%" }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Create Organization
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit}>
              <TextField
                fullWidth
                label="Organization Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Acme Sales Team"
                sx={{ mb: 2 }}
                slotProps={{
                  htmlInput: { minLength: 3, maxLength: 100 }
                }}
              />

              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={createOrg.isPending}
                size="large"
              >
                {createOrg.isPending ? "Creating..." : "Create Organization"}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
}
